import { Injectable } from '@angular/core';
import { ISectionData, PDFSectionIds, PrintUsingPdfTronDto, SectionIds } from '@modeso/aumico-lib-annualreport-fe';
import { IAnnualReport, IPrintOptions } from '@modeso/types__aumico-lib-annualreport-be';
import { cloneDeep } from 'lodash-es';
import { IPdfTronMapper } from '../../../interfaces/pdf-tron-mapper.interface';

@Injectable({
    providedIn: 'root',
})
export class ComplementaryReportPrintService {
    buildStandalonePrintOptions(
        basePrintOptions: IPrintOptions,
        request: { index: number; fileId?: string },
    ): IPrintOptions {
        const selection = {
            index: request.index,
            fileId: request.fileId,
            includeInReport: true,
        };

        return {
            ...cloneDeep<IPrintOptions>(basePrintOptions),
            displayOptions: {
                ...basePrintOptions.displayOptions,
                showCoverSheet: false,
                showTableOfContents: false,
                showNotesInDetailInToc: false,
                showCustomPageNumbers: false,
                showCompanyInfos: false,
                showCompanyLogo: false,
                showUIDonCover: false,
                showReportCompanyLogo: false,
                showComplementaryReports: true,
                complementaryReportsSelection: [selection],
                showAccountProofForBalance: false,
                showAccountProofForIncome: false,
            },
            reportOrder: [SectionIds.COMPLEMENTARY_REPORT + request.index],
        };
    }

    cloneAnnualReportForStandalone(
        annualReport: IAnnualReport,
        request: { index: number; fileId?: string },
    ): IAnnualReport {
        const annualReportForStandalone = cloneDeep<IAnnualReport>(annualReport);
        const selectedReport = annualReportForStandalone.complementaryReports?.[request.index];
        if (selectedReport) {
            selectedReport.showPageNumbers = false;
        }

        return annualReportForStandalone;
    }

    // Mutates printUsingPdfTronRequestData to scope it to a single complementary report section.
    applyStandaloneSectionToPrintData(
        pdfTronMapper: IPdfTronMapper,
        annualReportForStandalone: IAnnualReport,
        request: { index: number; fileId?: string },
        printUsingPdfTronRequestData: PrintUsingPdfTronDto,
    ): void {
        const complementaryReport = annualReportForStandalone.complementaryReports?.[request.index];
        if (!complementaryReport) {
            return;
        }

        const complementaryReportSection = pdfTronMapper.mapComplementaryReportToSectionData(
            complementaryReport,
            request.index.toString(),
        );
        const data = printUsingPdfTronRequestData.data;

        data.sections = [complementaryReportSection];

        // If the CRS being exported was part of an appendix section, preserve SECTION_APPENDIX
        const appendixIdx = printUsingPdfTronRequestData.orderedSectionsIds.indexOf(PDFSectionIds.APPENDIX);
        const appendixTitle = appendixIdx !== -1
            ? printUsingPdfTronRequestData.orderedSectionsTitles[appendixIdx]
            : null;

        printUsingPdfTronRequestData.orderedSectionsTitles = appendixTitle != null
            ? [appendixTitle, complementaryReportSection.title]
            : [complementaryReportSection.title];
        printUsingPdfTronRequestData.orderedSectionsIds = appendixTitle != null
            ? [PDFSectionIds.APPENDIX, complementaryReportSection.sectionId]
            : [complementaryReportSection.sectionId];
        data.hasBs = false;
        data.hasIs = false;
        data.hasCfs = false;
        data.hasEms = false;
        data.hasFcp = false;
        data.hasFas = false;
        data.hasNts = false;
        data.hasAOP = false;
        data.hasApbs = false;
        data.hasApis = false;
        data.apbs = undefined;
        data.apis = undefined;

        const filterBySectionId = (items?: ISectionData[] | null): ISectionData[] =>
            (items ?? []).filter((cr) => cr.sectionId === complementaryReportSection.sectionId);

        data.crs = filterBySectionId(data.crs);
        data.crsA01 = filterBySectionId(data.crsA01);
        data.crsA02 = filterBySectionId(data.crsA02);
        data.crsA03 = filterBySectionId(data.crsA03);

        printUsingPdfTronRequestData.data.options.showPageNumbers = false;
        printUsingPdfTronRequestData.data.options.isSealed = true;

        return;
    }
}
